import { useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { FadeUp, PageShell, Eyebrow, SectionHeading, EASE } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";

export default function ProjectDetail() {
  const c = useContent();
  const PROJECTS = c.projects;
  const { id } = useParams();
  const project = PROJECTS.find((p) => p.id === id);
  const [lightbox, setLightbox] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  if (!project) return <Navigate to="/projects" replace />;

  const idx = PROJECTS.indexOf(project);
  const next = PROJECTS[(idx + 1) % PROJECTS.length];

  const meta = [
    { label: "Architect", value: project.architect },
    { label: "Location", value: project.location },
    { label: "Duration", value: project.duration },
    { label: "Project Cost", value: project.cost },
    { label: "Scope", value: project.scope || "—" },
    { label: "Status", value: project.status },
  ];
  if (project.plot_area) meta.splice(3, 0, { label: "Plot Area", value: project.plot_area });
  if (project.builtup_area) meta.splice(project.plot_area ? 4 : 3, 0, { label: "Built-up Area", value: project.builtup_area });

  return (
    <PageShell testid="project-detail-page">
      <section ref={heroRef} className="relative h-[75vh] min-h-[520px] overflow-hidden flex items-end">
        <motion.div style={{ y }} className="absolute inset-0 -z-10">
          <img src={project.image} alt={project.title} className="w-full h-[115%] object-contain" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/20 to-obsidian" />
        </motion.div>
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-14">
          <FadeUp>
            <Link to="/projects" data-testid="back-to-projects-link" className="link-underline inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-bone/70 hover:text-bronze transition-colors duration-500 mb-8">
              <ArrowLeft size={14} /> All Projects
            </Link>
            <Eyebrow className="mb-4">{project.category} · {project.location}</Eyebrow>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08] text-bone max-w-3xl">{project.title}</h1>
          </FadeUp>
        </div>
      </section>

      <section className="border-b border-white/[0.07] bg-ink/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-8">
          {meta.map((m, i) => (
            <FadeUp key={m.label} delay={0.06 * i}>
              <p className="text-[10px] uppercase tracking-[0.3em] text-muted2 mb-2">{m.label}</p>
              <p className="text-sm text-bone font-light">{m.value}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <FadeUp>
              <Eyebrow className="mb-5">The Narrative</Eyebrow>
              <p className="font-mono text-xs text-muted2 tracking-wider">( From the Field )</p>
            </FadeUp>
          </div>
          <div className="lg:col-span-8 space-y-6">
            {project.description.map((para, i) => (
              <FadeUp key={i} delay={0.08 * i}>
                <p className={i === 0 ? "font-serif text-2xl sm:text-3xl font-light leading-snug text-bone" : "text-base font-light leading-relaxed text-stone2"}>
                  {para}
                </p>
              </FadeUp>
            ))}
            <FadeUp delay={0.2} className="pt-4 flex flex-wrap gap-3">
              {project.tags.map((m) => (
                <span key={m} className="text-[10px] uppercase tracking-[0.25em] text-bronze border border-bronze/30 px-4 py-2">{m}</span>
              ))}
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <SectionHeading eyebrow="Gallery" title="Details & atmosphere" className="mb-12" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {project.gallery.map((src, i) => (
            <FadeUp key={src + i} delay={0.08 * i}>
              <button
                data-testid={`gallery-image-${i}`}
                onClick={() => setLightbox(src)}
                className="group block w-full overflow-hidden relative"
              >
                <img src={src} alt={`${project.title} gallery ${i + 1}`} className="w-full h-auto transition-transform duration-700 ease-luxury group-hover:scale-105" />
                <div className="absolute inset-0 bg-obsidian/0 transition-colors duration-500 group-hover:bg-obsidian/20" />
              </button>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.07] bg-ink/40">
        <Link to={`/projects/${next.id}`} data-testid="next-project-link" className="group block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <FadeUp className="flex items-center justify-between gap-6">
            <div>
              <Eyebrow className="mb-4">Next Project</Eyebrow>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-bone transition-colors duration-500 group-hover:text-bronze">{next.title}</h2>
            </div>
            <span className="w-14 h-14 shrink-0 border border-bone/30 flex items-center justify-center text-bone transition-all duration-500 group-hover:bg-bronze group-hover:border-bronze group-hover:text-obsidian">
              <ArrowRight size={20} />
            </span>
          </FadeUp>
        </Link>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[90] bg-obsidian/95 backdrop-blur-md flex items-center justify-center p-6 cursor-zoom-out"
          >
            <motion.img
              initial={{ scale: 0.94 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, ease: EASE }}
              src={lightbox}
              alt="Gallery preview"
              className="max-w-full max-h-[85vh] object-contain"
            />
            <button data-testid="lightbox-close-btn" className="absolute top-6 right-6 w-11 h-11 border border-white/20 flex items-center justify-center text-bone hover:border-bronze hover:text-bronze transition-colors duration-500" aria-label="Close preview">
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </PageShell>
  );
}
